import { CaseClosingCheckDto, CloseMatterRequestDto } from 'types/caseClosing';
import { HistoryEntityType, HistoryFieldType } from 'types/histories';
import {
    MatterBillingSummaryDto,
    MatterCreateDto,
    MatterDeleteValidation,
    MatterDto,
    MatterListExportItem,
    MatterListItem,
    MatterListItemMin,
    MatterStateCountDto,
    MatterUpdateDto,
    NewCaseListItem,
    NewCasesParams,
    NewCasesSummary,
    UpdateMatterPricelistDto,
} from 'types/matters';
import {
    CreateMatterPricelistDto,
    MatterPricelist,
    MatterUninvoicedHours,
    PersonalRateInput,
    PricingKind,
    UpdateMatterPricelistHeaderDto,
} from 'types/pricelists';
import { objectToQueryParams } from 'utils/linkUtil';
import { QueryParams } from './common/types';
import { APIExperienceRecordsSlice } from './experience-records.ts';
import { APIInvoicesSlice } from './invoices.ts';
import { injectCountEndPoint, portalAPI } from './portalAPI';
import { accumulatedQueryWithFilters } from './util/endpointBuilder';

const EDITABLE_MATTERS_BATCH_SIZE = 500;

// Inject notifications tag to refetch them when adding case. So if you assign yourself you will get the notification instantly
export const APIMattersSlice = portalAPI
    .enhanceEndpoints({
        addTagTypes: [
            'Matter',
            'Notifications',
            'MattersListItem',
            'MattersMin',
            'MattersCount',
            'DashboardMatters',
            'FirmWideMatters',
            'DashboardMattersMin',
            'MatterCoiChecks',
            'MatterSummary',
            'MatterRates',
            'GetMatterStates',
            'HistoryListItems',
            'Timecards',
            'CalculatedPricelists',
            'CaseClosingCheck',
            'Delegation',
            'Invoices',
            'InvoiceMatters',
        ],
    })
    .injectEndpoints({
        endpoints: (builder) => ({
            getMatterById: builder.query<MatterDto, string>({
                query: (entityId) => `/v1/matters/${entityId}`,
                providesTags: (_1, _2, arg) => [{ type: 'Matter', id: arg }],
            }),
            getMatterRestrictedInfo: builder.query<MatterDto, string>({
                query: (entityId) => `/v1/matters/${entityId}/restricted-info`,
                keepUnusedDataFor: 0,
            }),
            getMattersList: accumulatedQueryWithFilters<MatterListItemMin>(builder, '/v1/matters/min', {
                providesTags: ['MattersMin'],
            }),
            getMattersWithFilters: accumulatedQueryWithFilters<MatterListItem>(builder, '/v1/matters', {
                providesTags: ['MattersListItem'],
                keepUnusedDataFor: 0,
            }),
            getMattersCount: injectCountEndPoint(builder, '/v1/matters', 'MattersCount'),
            getDashboardMattersWithFilters: accumulatedQueryWithFilters<MatterListItem>(
                builder,
                '/v1/matters/dashboard',
                {
                    providesTags: ['DashboardMatters'],
                }
            ),
            getFirmWideMattersWithFilters: accumulatedQueryWithFilters<MatterListItem>(
                builder,
                '/v1/matters/firmwide',
                {
                    providesTags: ['FirmWideMatters'],
                }
            ),
            // The NEW tab reads fresh on every mount and control change, so neither read is kept.
            getNewCasesSummary: builder.query<NewCasesSummary, NewCasesParams>({
                query: (params) => `/v1/matters/new-cases/summary${objectToQueryParams(params)}`,
                keepUnusedDataFor: 0,
            }),
            getNewCases: accumulatedQueryWithFilters<NewCaseListItem>(builder, '/v1/matters/new-cases', {
                keepUnusedDataFor: 0,
            }),
            getMattersExportList: builder.query<MatterListExportItem[], QueryParams>({
                query: ({ filter = '', sort = '' }) => ({
                    url: `/v1/matters/export${objectToQueryParams({ filter, sort })}`,
                }),
            }),
            getMatterBillingSummary: builder.query<MatterBillingSummaryDto, string | number>({
                query: (entityId) => `/v1/matters/${entityId}/billing-summary`,
                keepUnusedDataFor: 0,
                providesTags: ['MatterSummary'],
            }),
            getMattersStates: builder.query<MatterStateCountDto, { clientIds: number[]; filter?: string }>({
                query: ({ ...body }) => ({
                    url: `/v1/matters/states`,
                    method: 'PUT',
                    body,
                }),
                providesTags: ['GetMatterStates'],
            }),
            // Every price list of the case in date order, the base list first, each with its rows and cached figures.
            findMatterRates: builder.query<MatterPricelist[], string>({
                query: (entityId) => `/v1/matters/${entityId}/rates`,
                keepUnusedDataFor: 0,
                providesTags: (_1, _2, arg) => [{ type: 'MatterRates', id: arg }],
            }),
            canEditMatter: builder.query<boolean, number | string>({
                query: (entityId) => `/v1/matters/${entityId}/can-edit`,
                keepUnusedDataFor: 0,
            }),
            // The ids among the given ones the user may edit. The endpoint takes at most 500 ids, so a longer list is
            // asked in batches of that size.
            listEditableMatters: builder.query<number[], number[]>({
                queryFn: async (ids, _api, _extraOptions, baseQuery) => {
                    const batches = Array.from(
                        { length: Math.ceil(ids.length / EDITABLE_MATTERS_BATCH_SIZE) },
                        (_, i) => ids.slice(i * EDITABLE_MATTERS_BATCH_SIZE, (i + 1) * EDITABLE_MATTERS_BATCH_SIZE)
                    );
                    const results = await Promise.all(
                        batches.map((batch) => baseQuery(`/v1/matters/can-edit?ids=${batch.join(',')}`))
                    );
                    const failed = results.find((result) => result.error);
                    return failed?.error
                        ? { error: failed.error }
                        : { data: results.flatMap((result) => result.data as number[]) };
                },
                keepUnusedDataFor: 0,
            }),
            addMatter: builder.mutation<MatterDto, MatterCreateDto>({
                query: ({ ...body }) => ({
                    url: `/v1/matters`,
                    method: 'POST',
                    body,
                }),
                invalidatesTags: [
                    'Notifications',
                    'MattersListItem',
                    'MattersMin',
                    'DashboardMatters',
                    'FirmWideMatters',
                    'DashboardMattersMin',
                    'GetMatterStates',
                ],
            }),
            updateMatter: builder.mutation<MatterDto, MatterUpdateDto>({
                query: ({ id, ...body }) => ({
                    url: `/v1/matters/${id}`,
                    method: 'PUT',
                    body,
                }),
                // Matter save can create/archive notifications server-side
                // (e.g. the AML case block warning, or the closing notices on a reopen), so refetch them instantly
                invalidatesTags: (_1, _2, arg) =>
                    arg.id
                        ? [
                              'Notifications',
                              'MattersListItem',
                              'MatterSummary',
                              'MattersMin',
                              'DashboardMatters',
                              'FirmWideMatters',
                              'DashboardMattersMin',
                              'GetMatterStates',
                              'Timecards',
                              // The save writes the case's price lists too: every save sends the full set of lists,
                              // and a currency change deletes every list and recreates the base list.
                              'CalculatedPricelists',
                              { type: 'Matter', id: arg.id },
                              { type: 'MatterRates', id: arg.id },
                              { type: 'CaseClosingCheck', id: arg.id },
                              {
                                  type: 'HistoryListItems',
                                  historySelector: {
                                      entityType: HistoryEntityType.MATTER,
                                      entityId: arg.id,
                                      fieldType: HistoryFieldType.MATTER_STATE_TYPE_CODE,
                                  },
                              },
                              {
                                  type: 'HistoryListItems',
                                  historySelector: {
                                      entityType: HistoryEntityType.MATTER,
                                      entityId: arg.id,
                                      fieldType: HistoryFieldType.RESPONSIBLE_EMPLOYEE,
                                  },
                              },
                              {
                                  type: 'HistoryListItems',
                                  historySelector: {
                                      entityType: HistoryEntityType.MATTER,
                                      entityId: arg.id,
                                      fieldType: HistoryFieldType.SUPERVISING_EMPLOYEE,
                                  },
                              },
                          ]
                        : [
                              'Notifications',
                              'MattersListItem',
                              'MatterSummary',
                              'MattersMin',
                              'DashboardMatters',
                              'FirmWideMatters',
                              'DashboardMattersMin',
                              'GetMatterStates',
                              'Timecards',
                          ],
            }),
            updateMatterClientReference: builder.mutation<void, { id: number; value: string | null }>({
                query: ({ id, value }) => ({
                    url: `/v1/matters/${id}/client-reference`,
                    method: 'PUT',
                    body: { value },
                }),
                invalidatesTags: (_1, _2, arg) => [{ type: 'Matter', id: arg.id }],
                async onQueryStarted(_, { dispatch, queryFulfilled }) {
                    await queryFulfilled;
                    dispatch(APIInvoicesSlice.util.invalidateTags(['Invoices']));
                },
            }),
            updateMatterCaseNumber: builder.mutation<void, { id: number; value: string }>({
                query: ({ id, value }) => ({
                    url: `/v1/matters/${id}/case-number`,
                    method: 'PUT',
                    body: { value },
                }),
                invalidatesTags: (_1, _2, arg) => [{ type: 'Matter', id: arg.id }, 'MattersListItem', 'MattersMin'],
            }),
            updateMatterSequenceNumber: builder.mutation<void, { id: number; value: string }>({
                query: ({ id, value }) => ({
                    url: `/v1/matters/${id}/sequence-number`,
                    method: 'PUT',
                    body: { value },
                }),
                invalidatesTags: (_1, _2, arg) => [{ type: 'Matter', id: arg.id }, 'MattersListItem', 'MattersMin'],
            }),
            // The endpoint takes the Offer Won case id, while the link itself is stored on the case opened from it,
            // so both cases have to be invalidated.
            unlinkConnectedCase: builder.mutation<void, { offerCaseId: number; connectedCaseId: number }>({
                query: ({ offerCaseId }) => ({
                    url: `/v1/matters/${offerCaseId}/originates-from-matter`,
                    method: 'DELETE',
                }),
                invalidatesTags: (_1, _2, arg) => [
                    { type: 'Matter', id: arg.offerCaseId },
                    { type: 'Matter', id: arg.connectedCaseId },
                ],
            }),
            // Reopening takes the Case out of a final state, so the lists and counts that exclude final states,
            // and the timecard editability those states drive, all change too.
            reopenMatter: builder.mutation<MatterDto, { id: number }>({
                query: ({ id }) => ({
                    url: `/v1/matters/${id}/reopen`,
                    method: 'PUT',
                }),
                invalidatesTags: (_1, _2, arg) => [
                    'MattersListItem',
                    'MattersMin',
                    'DashboardMatters',
                    'FirmWideMatters',
                    'DashboardMattersMin',
                    'GetMatterStates',
                    'Timecards',
                    { type: 'Matter', id: arg.id },
                    {
                        type: 'HistoryListItems',
                        historySelector: {
                            entityType: HistoryEntityType.MATTER,
                            entityId: String(arg.id),
                            fieldType: HistoryFieldType.MATTER_STATE_TYPE_CODE,
                        },
                    },
                ],
            }),
            getCaseClosingCheck: builder.query<CaseClosingCheckDto, number>({
                query: (matterId) => `/v1/matters/${matterId}/closing-check`,
                keepUnusedDataFor: 0,
                providesTags: (_1, _2, matterId) => [{ type: 'CaseClosingCheck', id: matterId }],
            }),
            // Closing changes the same lists, counts and timecard editability as a reopen, archives the case's closing
            // notices, drops its delegation covers and withdraws the close-on-send tick from its invoices. RTK also
            // invalidates on a refused close, which changes nothing; the modal reloads its check itself then.
            closeCase: builder.mutation<MatterDto, { matterId: number } & CloseMatterRequestDto>({
                query: ({ matterId, ...body }) => ({
                    url: `/v1/matters/${matterId}/close`,
                    method: 'POST',
                    body,
                }),
                invalidatesTags: (_1, error, arg) =>
                    error
                        ? []
                        : [
                              'MattersListItem',
                              'MattersMin',
                              'MattersCount',
                              'DashboardMatters',
                              'DashboardMattersMin',
                              'FirmWideMatters',
                              'GetMatterStates',
                              'Timecards',
                              'Notifications',
                              'Delegation',
                              'Invoices',
                              'InvoiceMatters',
                              { type: 'Matter', id: arg.matterId },
                              {
                                  type: 'HistoryListItems',
                                  historySelector: {
                                      entityType: HistoryEntityType.MATTER,
                                      entityId: String(arg.matterId),
                                      fieldType: HistoryFieldType.MATTER_STATE_TYPE_CODE,
                                  },
                              },
                          ],
            }),
            // The four per-list writes leave the rates read alone on purpose: the case editor commits several of
            // them in one Save and invalidates the rates once at the end. A refetch started by an earlier write
            // would still be pending when a later write asked for one, and a pending query drops that request,
            // which left the page showing the state after the first write.
            updateMatterPricelist: builder.mutation<MatterDto, Partial<UpdateMatterPricelistDto> & { id: string }>({
                query: ({ id, ...body }) => ({
                    url: `/v1/matters/${id}/pricelist`,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: ['MatterSummary', 'CalculatedPricelists'],
            }),
            createMatterPricelist: builder.mutation<number, CreateMatterPricelistDto & { id: string }>({
                query: ({ id, ...body }) => ({
                    url: `/v1/matters/${id}/pricelists`,
                    method: 'POST',
                    body,
                }),
                invalidatesTags: ['MatterSummary', 'CalculatedPricelists'],
            }),
            updateMatterPricelistHeader: builder.mutation<
                void,
                UpdateMatterPricelistHeaderDto & { id: string; pricelistId: number }
            >({
                query: ({ id, pricelistId, ...body }) => ({
                    url: `/v1/matters/${id}/pricelists/${pricelistId}`,
                    method: 'PATCH',
                    body,
                }),
                invalidatesTags: ['MatterSummary', 'CalculatedPricelists'],
            }),
            deleteMatterPricelist: builder.mutation<void, { id: string; pricelistId: number }>({
                query: ({ id, pricelistId }) => ({
                    url: `/v1/matters/${id}/pricelists/${pricelistId}`,
                    method: 'DELETE',
                }),
                invalidatesTags: ['MatterSummary', 'CalculatedPricelists'],
            }),
            // Following a list or making an own copy of it; the rates read is left to the caller, as above.
            updateMatterPricingLink: builder.mutation<
                MatterPricelist[],
                { id: string; followsPricelist: { id: number } | null }
            >({
                query: ({ id, followsPricelist }) => ({
                    url: `/v1/matters/${id}/pricing-link`,
                    method: 'PUT',
                    body: { followsPricelist },
                }),
                invalidatesTags: (_1, _2, arg) => [
                    'MatterSummary',
                    'CalculatedPricelists',
                    'MattersListItem',
                    { type: 'Matter', id: arg.id },
                ],
            }),
            updateMatterPricingKind: builder.mutation<MatterPricelist[], { id: string; pricingKind: PricingKind }>({
                query: ({ id, pricingKind }) => ({
                    url: `/v1/matters/${id}/pricing-kind`,
                    method: 'PUT',
                    body: { pricingKind },
                }),
                invalidatesTags: (_1, _2, arg) => [
                    'MatterSummary',
                    'CalculatedPricelists',
                    'MattersListItem',
                    { type: 'Matter', id: arg.id },
                ],
            }),
            getMatterUninvoicedHours: builder.query<MatterUninvoicedHours, { id: string; employeeIds?: number[] }>({
                query: ({ id, employeeIds }) => ({
                    url: `/v1/matters/${id}/uninvoiced-hours`,
                    params: employeeIds ? { employeeIds: employeeIds.join(',') } : undefined,
                }),
                keepUnusedDataFor: 0,
            }),
            // The full set of a following case's price exceptions; the rates read is left to the caller, as above.
            updateMatterPricelistExceptions: builder.mutation<
                MatterPricelist[],
                { id: string; exceptions: PersonalRateInput[] }
            >({
                query: ({ id, exceptions }) => ({
                    url: `/v1/matters/${id}/pricelist-exceptions`,
                    method: 'PUT',
                    body: { exceptions },
                }),
                invalidatesTags: (_1, _2, arg) => [
                    'MatterSummary',
                    'CalculatedPricelists',
                    'MattersListItem',
                    { type: 'Matter', id: arg.id },
                ],
            }),
            updateMatterPricelistCustomName: builder.mutation<void, { id: string; name: string | null }>({
                query: ({ id, name }) => ({
                    url: `/v1/matters/${id}/pricelist-custom-name`,
                    method: 'PUT',
                    body: { name },
                }),
                invalidatesTags: ['MatterSummary', 'CalculatedPricelists'],
            }),
            updateMatterExperienceRecordExclusion: builder.mutation<boolean, { id: string; isExcludedFromXR: boolean }>(
                {
                    query: ({ id, isExcludedFromXR }) => ({
                        url: `/v1/matters/${id}/experience-record-exclusion`,
                        method: 'PUT',
                        params: { isExcludedFromXR },
                    }),
                    invalidatesTags: (_1, _2, arg) => [
                        'MattersListItem',
                        'MattersMin',
                        'DashboardMatters',
                        'FirmWideMatters',
                        'DashboardMattersMin',
                        { type: 'Matter', id: arg.id },
                    ],
                    async onQueryStarted(_, { dispatch, queryFulfilled }) {
                        await queryFulfilled;
                        dispatch(APIExperienceRecordsSlice.util.invalidateTags(['ExperienceRecordListItems']));
                    },
                }
            ),
            bulkUpdateMatterExperienceRecordExclusion: builder.mutation<
                boolean,
                { id: number; isExcludedFromXR: boolean }[]
            >({
                query: (updates) => ({
                    url: `/v1/matters/bulk/experience-record-exclusion`,
                    method: 'PUT',
                    body: updates,
                }),
                invalidatesTags: [
                    'MattersListItem',
                    'MattersMin',
                    'DashboardMatters',
                    'FirmWideMatters',
                    'DashboardMattersMin',
                ],
                async onQueryStarted(_, { dispatch, queryFulfilled }) {
                    await queryFulfilled;
                    dispatch(APIExperienceRecordsSlice.util.invalidateTags(['ExperienceRecordListItems']));
                },
            }),
            deleteMatter: builder.mutation<boolean, { id: string }>({
                query: ({ id }) => ({
                    url: `/v1/matters/${id}`,
                    method: 'DELETE',
                }),
                invalidatesTags: [
                    'MattersListItem',
                    'MattersMin',
                    'DashboardMatters',
                    'FirmWideMatters',
                    'DashboardMattersMin',
                    'GetMatterStates',
                ],
            }),
            getMatterDeleteValidation: builder.query<MatterDeleteValidation, { id: string }>({
                query: ({ id }) => `/v1/matters/${id}/delete-validation`,
                keepUnusedDataFor: 0,
            }),
        }),
        overrideExisting: false,
    });

export const {
    useGetMatterByIdQuery,
    useGetMatterRestrictedInfoQuery,
    useGetMattersListQuery,
    useLazyGetMattersListQuery,
    useGetMattersStatesQuery,
    useGetMatterBillingSummaryQuery,
    useFindMatterRatesQuery,
    useLazyFindMatterRatesQuery,
    useCreateMatterPricelistMutation,
    useUpdateMatterPricelistHeaderMutation,
    useDeleteMatterPricelistMutation,
    useCanEditMatterQuery,
    useListEditableMattersQuery,
    useLazyGetMattersCountQuery,
    useLazyGetMattersWithFiltersQuery,
    useLazyGetDashboardMattersWithFiltersQuery,
    useLazyGetFirmWideMattersWithFiltersQuery,
    useGetNewCasesSummaryQuery,
    useLazyGetNewCasesQuery,
    useLazyGetMattersExportListQuery,
    useAddMatterMutation,
    useUpdateMatterMutation,
    useUpdateMatterClientReferenceMutation,
    useUpdateMatterCaseNumberMutation,
    useUpdateMatterSequenceNumberMutation,
    useUnlinkConnectedCaseMutation,
    useReopenMatterMutation,
    useGetCaseClosingCheckQuery,
    useLazyGetCaseClosingCheckQuery,
    useCloseCaseMutation,
    useUpdateMatterPricelistMutation,
    useUpdateMatterPricingLinkMutation,
    useUpdateMatterPricelistExceptionsMutation,
    useUpdateMatterPricingKindMutation,
    useLazyGetMatterUninvoicedHoursQuery,
    useUpdateMatterPricelistCustomNameMutation,
    useUpdateMatterExperienceRecordExclusionMutation,
    useBulkUpdateMatterExperienceRecordExclusionMutation,
    useDeleteMatterMutation,
    useLazyGetMatterDeleteValidationQuery,
    useGetMattersWithFiltersQuery,
} = APIMattersSlice;
